import Footer from 'views/Footer'
import { useApi } from 'providers/ApiProvider'
import { useAuth } from 'providers/AuthProvider'
import { useCallback, useEffect, useState } from 'react'
import {
  Quiz,
  QuizComment,
  QuizQuestion,
  ReviewerComment,
} from 'apis/entities/quiz.entity'

import Spinner from 'views/Spinner'
import QuestionView, { QuestionViewRef } from 'views/QuestionView'
import { StyleUtil } from 'utils/StyleUtil'
import { ToastUtil } from 'utils/ToastUtil'
import React from 'react'
import { useData } from 'providers/DataProvider'
import ModalTopicLink from 'views/ModalTopicLInk'

export default function HomePage() {
  const {
    getQuiz,
    getQuizReviewed,
    addComments,
    getReviewLog,
    saveReviewCache,
    getReviewCache,
    exportQuiz,
  } = useApi()
  const { reviewerId, setReviewerId } = useAuth()
  const [quiz, setQuiz] = useState<Quiz | null>(null)
  const [questions, setQuestions] = useState<QuizQuestion[]>([])

  const [isFetching, setIsFetching] = useState<boolean>(true)
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false)
  const [isReviewed, setIsReviewed] = useState<boolean>(false)
  const [reviewedAt, setReviewedAt] = useState<Date | null>(null)
  const refReviewVersion = React.useRef<number | undefined>(undefined)
  const refs: QuestionViewRef[] = []
  const [showWarningModal, setShowWarningModal] = useState<boolean>(false)
  const [highlightedQuestions, setHighlightedQuestions] = useState<string>('')
  const [submittable, setSubmittable] = useState<boolean>(false)
  const {
    setQuiz: setQuizForCache,
    getDataByQuestionId,
    clearData,
    saveData,
    getData,
    setData,
  } = useData()
  const [isSaving, setIsSaving] = useState<boolean>(false)
  const [topicLink, setTopicLink] = useState<string>('')
  const [showModalTopicLink, setShowModalTopicLink] = useState<boolean>(false)

  const getHighlightedQuestionNumbers = (): number[] => {
    const questionNumbers: number[] = []
    refs.forEach((ref, index) => {
      // console.log(ref.approved, ref.comment)
      if (
        ref.approved === null ||
        (ref.approved === false && ref.comment === '')
      ) {
        questionNumbers.push(index + 1)
      }
    })
    return questionNumbers
  }

  const fetchReviewCache = useCallback(async () => {
    try {
      const res = await getReviewCache()
      if (res) {
        const cache = JSON.parse(res)
        console.log(cache)
        // override local cache from server side if no cache
        if (!getData()) {
          setData(cache)
        }
      }
    } catch (error) {
      console.log(error)
    }
  }, [getData, getReviewCache, setData])

  const fetchQuiz = useCallback(
    async (reviewed: boolean, version?: number | undefined) => {
      setIsFetching(true)
      try {
        let res: Quiz
        if (reviewed) {
          res = await getQuizReviewed(version)
        } else {
          res = await getQuiz(version)
        }
        // console.log(res)
        setQuizForCache(res)
        setQuiz(res)
        setQuestions(res.questions)
      } catch (error) {
        console.log(error)
      } finally {
        setIsFetching(false)
      }
    },
    [getQuiz, getQuizReviewed, setQuizForCache],
  )

  const fetchReviewLog = useCallback(async () => {
    let version: number | undefined
    let reviewed = false
    try {
      const res = await getReviewLog()
      // console.log(res)
      if (res) {
        // return reviewer id by token
        setReviewerId(res.reviewer.id)
        // set reviewed at
        setReviewedAt(new Date(res.createdAt))
        // no log found
        reviewed = res.id !== undefined && res.id !== null
        setIsReviewed(reviewed)
        // get quiz version
        version = res.version
        refReviewVersion.current = version
      }
    } catch (error) {
      console.log(error)
    } finally {
      if (!reviewed) {
        await fetchReviewCache()
      }
      await fetchQuiz(reviewed, version)
    }
  }, [fetchQuiz, fetchReviewCache, getReviewLog, setReviewerId])

  const onClickConfirmAll = async () => {
    // check if all questions are approved or rejected with comments
    const isAllReviewed = refs.every(
      (ref) =>
        ref.approved === true || (ref.approved === false && ref.comment !== ''),
    )
    if (!isAllReviewed) {
      const highligtedQuestionNumbers = getHighlightedQuestionNumbers()
      const highligtedQuestionNumbersString =
        highligtedQuestionNumbers.join(', ')
      // highlight the comments if rejected
      refs.forEach((ref, index) => {
        if (ref.approved === false) {
          ref.toggleHighlightComment(ref.comment === '')
        }
        const highlighted = highligtedQuestionNumbers.includes(index + 1)
        // console.log(`Question ${index + 1} is highlighted: ${highlighted}`)
        ref.toggleHighlightQuestion(highlighted)
      })
      setHighlightedQuestions(highligtedQuestionNumbersString)
      // next tick to show the modal after the highlight
      setTimeout(() => {
        setShowWarningModal(true)
      }, 0)
      return
    }

    try {
      // post comments
      const comments = refs.map(
        (ref) =>
          ({
            questionId: ref.question.id,
            text: ref.approved === true ? '' : ref.comment,
            approved: ref.approved,
            version: quiz?.version,
          } as ReviewerComment),
      )
      setIsSubmitting(true)
      await addComments(comments)
      ToastUtil.success('Submitted successfully')
      // reload to update status
      fetchReviewLog()
      clearData()
    } catch (error) {
      console.log(error)
      ToastUtil.error('Failed to submit')
    } finally {
      setIsSubmitting(false)
    }
  }

  const getPreivousComment = (question: QuizQuestion): QuizComment | null => {
    if (!question.comments || question.comments.length === 0) return null
    // find the last comment by reviewer
    const comments = question.comments
      .filter((comment) => comment.reviewer.id === reviewerId)
      // sort by created time desc
      .sort((a, b) => {
        return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
      })
    // console.log(comments)

    // find the specific comment by version for reviewed quiz
    if (refReviewVersion.current) {
      const comment = comments.find(
        (comment) => comment.version === refReviewVersion.current,
      )
      if (comment) return comment
    }
    // return the last comment
    const comment = comments[0]
    // console.log(comment)
    return comment || null
  }

  const renderTitle = (): string => {
    if (isReviewed) {
      return 'Thank you for reviewing this quiz'
    } else {
      return 'Welcome to our question review tool'
    }
  }

  const renderDescription = (): string => {
    if (isReviewed) {
      const date = reviewedAt || new Date()
      // convert to date format 15 Jan 2024 17:06
      const timestamp = date.toLocaleString('en-GB', {
        day: 'numeric',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      })
      return `Your feedback was shared with the UNSW team on ${timestamp}`
    } else {
      return `Thank you for taking the time to review this proposed quiz content. 
      You can click "approve" or "reject" for each proposed question, and can also 
      add comments or suggestions in the comment box. <br />
      If this is a second review, you will see a toggle button 
      to track changes based on initial feedback.`
    }
  }

  const hasEditedQuestions = (): boolean => {
    return questions.some((question) => {
      if (!question.previous) return false
      return (
        question.text !== question.previous?.text ||
        JSON.stringify(question.answers) !==
          JSON.stringify(question.previous?.answers) ||
        question.learningOutcome !== question.previous?.learningOutcome
      )
    })
  }

  const renderEditedMessage = (): string => {
    const questionNumbers: string[] = []
    questions.forEach((question, index) => {
      // double check if the question is edited
      const edited =
        question.text !== question.previous?.text ||
        JSON.stringify(question.answers) !==
          JSON.stringify(question.previous?.answers) ||
        question.learningOutcome !== question.previous?.learningOutcome
      // console.log(edited)

      if (edited) {
        questionNumbers.push(`#${index + 1}`)
      }
    })
    return `The following questions have been edited:  ${questionNumbers.join(
      ', ',
    )}`
  }

  const onReviewChanged = (animated: boolean) => {
    // next tick to update the state after the review
    setTimeout(() => {
      const isAllReviewed = refs.every(
        (ref) =>
          ref.approved === true ||
          (ref.approved === false && ref.comment !== ''),
      )
      setSubmittable(isAllReviewed)
      if (isAllReviewed && animated) {
        // scroll to the bottom with animation
        window.scrollTo({
          top: document.body.scrollHeight,
          behavior: 'smooth',
        })
      }
    }, 0)
  }

  const onClickSaveAndContinueLater = async () => {
    // console.log('Save and continue later')
    saveData()
    const cache = getData()
    if (cache) {
      try {
        setIsSaving(true)
        await saveReviewCache(cache)
        setIsSaving(false)
        ToastUtil.success(
          'Your work is saved, you can continue later. Your feedback will not be shared with UNSW until you click "Submit".',
          {
            width: '540px',
            right: '80%',
          },
        )
      } catch (error) {
        console.log(error)
      }
    }
  }

  const onExportQuiz = async () => {
    if (!quiz) return
    try {
      const blob = await exportQuiz()
      const url = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      const quizName = quiz?.name.replace(/ /g, '_')
      const date = new Date()
      const dateStr = date.toISOString().split('T')[0]
      const filename = `${quizName}_${dateStr}.xlsx`

      link.setAttribute('download', filename)
      document.body.appendChild(link)

      // Trigger the download
      link.click()

      // Cleanup
      link.parentNode?.removeChild(link)
      window.URL.revokeObjectURL(url)
    } catch (error) {
      ToastUtil.error('Failed to export quiz')
    }
  }

  useEffect(() => {
    fetchReviewLog()
  }, [fetchReviewLog])

  return (
    <>
      <div className="flex flex-col items-center">
        {isFetching && (
          <div className="flex h-screen items-center justify-center">
            <Spinner />
          </div>
        )}
        {!isFetching && (
          <>
            <div className="mt-[120px] w-[90%]">
              {!quiz && (
                <div className="flex flex-col items-center">
                  <h1 className="mb-[120px] text-center text-3xl font-bold">
                    No quiz found
                  </h1>
                </div>
              )}
              {quiz && questions && (
                <div className="flex flex-col gap-[24px]">
                  <p className="header">{renderTitle()}</p>
                  <div className="flex flex-row">
                    <p
                      className="description pr-6"
                      dangerouslySetInnerHTML={{
                        __html: renderDescription(),
                      }}
                    ></p>
                    <div className="grow" />
                    <div className="flex flex-col items-end pr-[8%]">
                      <button
                        className={`${StyleUtil.buttonPrimaryUnsw} whitespace-nowrap`}
                        onClick={onExportQuiz}
                      >
                        Export quiz
                      </button>
                    </div>
                  </div>
                  {hasEditedQuestions() && (
                    <p className="description">{renderEditedMessage()}</p>
                  )}
                  <div className="">
                    <p className="topic">
                      This is {quiz.name}
                      {quiz.courseName ? ' from course ' + quiz.courseName : ''}
                    </p>
                    {/*<p className="description">*/}
                    {/*  {quiz.description?.split('\n').map((line, index) => (*/}
                    {/*    <React.Fragment key={index}>*/}
                    {/*      {line}*/}
                    {/*      <br />*/}
                    {/*    </React.Fragment>*/}
                    {/*  ))}*/}
                    {/*</p>*/}
                  </div>
                  <div className="questions-container">
                    <div className="questions-list">
                      {questions.map((question, index) => (
                        <QuestionView
                          key={index}
                          index={index}
                          quizVersion={quiz.version}
                          question={question}
                          isReviewed={isReviewed}
                          previousComment={getPreivousComment(question)}
                          cacheData={getDataByQuestionId(question.id)}
                          onChanged={onReviewChanged}
                          onClickTopicLink={(url) => {
                            setTopicLink(url)
                            setShowModalTopicLink(true)
                          }}
                          ref={(el: QuestionViewRef) => (refs[index] = el)}
                        />
                      ))}
                      {!isReviewed && (
                        <div className="mt-[38px]">
                          {isSaving && <Spinner />}
                          {!isSaving && (
                            <button
                              className={StyleUtil.buttonBlackWhite}
                              onClick={onClickSaveAndContinueLater}
                            >
                              Save and continue later
                            </button>
                          )}
                        </div>
                      )}
                    </div>
                    {isSubmitting && (
                      <div className="flex items-center justify-center">
                        <Spinner />
                      </div>
                    )}
                    {!isSubmitting && !isReviewed && (
                      <button
                        className={`${
                          submittable
                            ? StyleUtil.buttonPrimaryUnsw
                            : StyleUtil.buttonBlackWhite
                        }`}
                        onClick={onClickConfirmAll}
                      >
                        Submit
                      </button>
                    )}
                  </div>
                </div>
              )}
            </div>
            {showWarningModal && (
              <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
                <div className="flex w-[700px] flex-col items-center gap-6 bg-white px-4 pb-6 pt-3">
                  <div
                    className="cursor-pointer self-end"
                    onClick={() => setShowWarningModal(false)}
                  >
                    <svg
                      width="12"
                      height="12"
                      viewBox="0 0 12 12"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        fillRule="evenodd"
                        clipRule="evenodd"
                        d="M7.41189 6.00019L11.7119 1.71019C12.104 1.31806 12.104 0.682305 11.7119 0.290185C11.3198 -0.101937 10.684 -0.101937 10.2919 0.290185L6.00189 4.59019L1.71189 0.290185L1.71189 0.290185C1.31977 -0.101937 0.684012 -0.101937 0.291892 0.290185C-0.10023 0.682307 -0.10023 1.31807 0.291892 1.71019L4.59189 6.00019L0.291892 10.2902C-0.10023 10.6791 -0.102826 11.3123 0.286094 11.7044C0.288018 11.7063 0.289951 11.7083 0.291892 11.7102H0.291892C0.680812 12.1023 1.31397 12.1049 1.70609 11.716C1.70803 11.7141 1.70997 11.7121 1.71189 11.7102L6.00189 7.41018L10.2919 11.7102V11.7102C10.6808 12.1023 11.314 12.1049 11.7061 11.716C11.708 11.7141 11.71 11.7121 11.7119 11.7102C12.104 11.3213 12.1066 10.6881 11.7177 10.296C11.7158 10.294 11.7138 10.2921 11.7119 10.2902L7.41189 6.00019Z"
                        fill="#51597B"
                      />
                    </svg>
                  </div>
                  <div className="flex flex-row gap-5 px-4">
                    <div className="pt-1">
                      <svg
                        width="36"
                        height="34"
                        viewBox="0 0 36 34"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M35.4856 27.3L21.4319 2.89362C21.0807 2.29567 20.5793 1.79989 19.9775 1.4554C19.3757 1.11091 18.6943 0.929688 18.0008 0.929688C17.3074 0.929687 16.6259 1.11091 16.0241 1.4554C15.4223 1.79989 14.9209 2.29567 14.5697 2.89362L0.515983 27.3C0.178076 27.8784 0 28.5362 0 29.206C0 29.8759 0.178076 30.5336 0.515983 31.112C0.862675 31.7136 1.36318 32.212 1.96613 32.5563C2.56909 32.9006 3.25278 33.0782 3.94706 33.071H32.0545C32.7483 33.0776 33.4313 32.8997 34.0336 32.5555C34.636 32.2113 35.136 31.7131 35.4824 31.112C35.8208 30.5339 35.9994 29.8763 36 29.2065C36.0006 28.5366 35.823 27.8787 35.4856 27.3ZM33.2566 29.8247C33.1341 30.0337 32.9582 30.2064 32.747 30.325C32.5357 30.4436 32.2968 30.5039 32.0545 30.4997H3.94706C3.70484 30.5039 3.46586 30.4436 3.25462 30.325C3.04339 30.2064 2.86749 30.0337 2.74498 29.8247C2.634 29.6369 2.57546 29.4226 2.57546 29.2044C2.57546 28.9862 2.634 28.772 2.74498 28.5841L16.7987 4.17766C16.9237 3.96966 17.1004 3.79755 17.3116 3.67806C17.5228 3.55857 17.7614 3.49578 18.004 3.49578C18.2467 3.49578 18.4852 3.55857 18.6964 3.67806C18.9076 3.79755 19.0843 3.96966 19.2093 4.17766L33.2631 28.5841C33.3731 28.7726 33.4305 28.9871 33.4294 29.2053C33.4282 29.4235 33.3686 29.6374 33.2566 29.8247ZM16.7152 20.2145V13.7863C16.7152 13.4453 16.8506 13.1183 17.0917 12.8772C17.3328 12.6361 17.6598 12.5006 18.0008 12.5006C18.3418 12.5006 18.6688 12.6361 18.9099 12.8772C19.151 13.1183 19.2865 13.4453 19.2865 13.7863V20.2145C19.2865 20.5555 19.151 20.8825 18.9099 21.1236C18.6688 21.3647 18.3418 21.5002 18.0008 21.5002C17.6598 21.5002 17.3328 21.3647 17.0917 21.1236C16.8506 20.8825 16.7152 20.5555 16.7152 20.2145ZM19.9293 25.9999C19.9293 26.3814 19.8162 26.7542 19.6043 27.0713C19.3924 27.3885 19.0912 27.6356 18.7388 27.7816C18.3864 27.9276 17.9987 27.9658 17.6246 27.8914C17.2505 27.8169 16.9069 27.6333 16.6372 27.3636C16.3675 27.0939 16.1838 26.7502 16.1094 26.3762C16.035 26.0021 16.0732 25.6143 16.2191 25.2619C16.3651 24.9096 16.6123 24.6084 16.9294 24.3965C17.2465 24.1846 17.6194 24.0715 18.0008 24.0715C18.5123 24.0715 19.0028 24.2746 19.3644 24.6363C19.7261 24.998 19.9293 25.4885 19.9293 25.9999Z"
                          fill="#FF635D"
                        />
                      </svg>
                    </div>
                    <p className="highlight-message">
                      Please review the following question number(s) before
                      submitting:{' '}
                      <span className="highlight-question">Question </span>
                      <span className="highlight-question">
                        {highlightedQuestions}
                      </span>
                      <span className="highlight-question">.</span> You need to
                      reject or approve all questions to submit your feedback.
                    </p>
                  </div>
                  <button
                    className={StyleUtil.buttonBlackWhite}
                    onClick={() => {
                      setShowWarningModal(false)
                      // scroll to the first highlighted question
                      try {
                        refs[
                          parseInt(highlightedQuestions.split(',')[0]) - 1
                        ].scrollIntoView()
                      } catch (error) {
                        console.log(error)
                      }
                    }}
                  >
                    Ok
                  </button>
                </div>
              </div>
            )}
          </>
        )}
        {!isFetching && <Footer />}
      </div>
      {showModalTopicLink && (
        <ModalTopicLink
          url={topicLink}
          onClose={() => setShowModalTopicLink(false)}
        />
      )}
    </>
  )
}
